import type { FC } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { SlideOut } from '@interstate/components/SlideOut';
import { configActionCreators, dealerSelectors } from '@makemydeal/dr-shared-store';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import TemplateSaveDrawerContent from './TemplateSaveDrawerContent';
import { TEST_IDS, Tab } from './constants';
import { useGetDrawerWidth } from '../../dealActivity/activities/hooks/useGetDrawerWidth';
import { dealLockHooks } from '../../hooks';
import CantBuildTemplateModal from '../cantBuildTemplateModal/CantBuildTemplateModal';
import { createManageTemplatesEvent } from '../analytics/cdlEventCreators';

type SaveStatusDialogProps = {
    disabled: boolean;
};

export const TemplateButtonsBarWithBuilderMode: FC<SaveStatusDialogProps> = ({ disabled }) => {
    const panelWidth = useGetDrawerWidth({ extraLarge: 1320, large: 1128, tablet: 900 });
    const { show: drawerShown, builderModeTab: defaultDrawerTab } = useSelector(dealTemplateSelectors.getTemplateBuilderMode) || {};
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const hasMixedVehicles = useSelector(dealTemplateSelectors.getHasMultipleVehiclesAcrossScenarios);
    const [showCantBuildModal, setShowCantBuildModal] = useState(false);

    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const correlationId = useSelector(offerReduxSelectors.getSessionId);

    const handleSaveTemplateClick = () => {
        if (hasMixedVehicles) {
            setShowCantBuildModal(true);
            logNewRelicPageAction('MV:MultiScenarioTemplate - Create Blocked Different Vehicles', {
                applicationName: 'MV',
                correlationId,
                dealXgId,
                dealXgVersion,
                dealerId,
                vin
            });
        } else {
            dispatch(dealTemplateActionCreators.triggerTemplateBuilderMode(true, Tab.Save));
        }
    };

    const handleManageTemplatesClick = () => {
        dispatch(createManageTemplatesEvent());
        dispatch(dealTemplateActionCreators.triggerTemplateBuilderMode(true, Tab.Manage));
    };

    return (
        <>
            {!isLocked && (
                <Box display="flex" justifyContent="flex-end" width="100%">
                    <Button
                        buttonStyle="tertiary"
                        sx={{ mr: 2 }}
                        onClick={() => {
                            dispatch(configActionCreators.turnOffTemplateView());
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        buttonStyle="secondary"
                        sx={{ mr: 2 }}
                        data-testid={TEST_IDS.OPEN_DRAWER_TO_MANAGE_TEMPLATES}
                        onClick={handleManageTemplatesClick}
                    >
                        Manage Templates
                    </Button>
                    <Button
                        onClick={handleSaveTemplateClick}
                        data-testid={TEST_IDS.OPEN_DRAWER_TO_SAVE_TEMPLATE}
                        size="medium"
                        disabled={disabled}
                    >
                        Save Template
                    </Button>
                </Box>
            )}
            <SlideOut
                data-testid={TEST_IDS.DEAL_TEMPLATE_DRAWER}
                header="Deal Templates"
                show={drawerShown}
                position="right"
                panelWidth={panelWidth}
                onHide={() => dispatch(dealTemplateActionCreators.triggerTemplateBuilderMode(false))}
            >
                <TemplateSaveDrawerContent
                    handleCancel={() => dispatch(dealTemplateActionCreators.triggerTemplateBuilderMode(false))}
                    defaultDrawerTab={(defaultDrawerTab || Tab.Save) as Tab}
                />
            </SlideOut>
            <CantBuildTemplateModal show={showCantBuildModal} onHide={() => setShowCantBuildModal(false)} />
        </>
    );
};
